import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { Logo, IconAlert, IconLoader, IconCheck, IconSearch } from "../components/Icons";
import LanguageFlags from "../components/LanguageFlags";

interface AresResult {
  ico: string;
  obchodni_jmeno: string;
  dic: string | null;
  address_street: string | null;
  city: string | null;
  postal_code: string | null;
  country: string;
}

export default function Register() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    email: "", password: "", password2: "", company_name: "", full_name: "",
    billing_address: "", billing_city: "", billing_postal_code: "",
    billing_country: "CZ", company_id: "", vat_id: "",
  });
  const [seedDemo, setSeedDemo] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [aresStatus, setAresStatus] = useState<"idle" | "loading" | "ok" | "notfound" | "error">("idle");
  const [aresMsg, setAresMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const nav = useNavigate();

  useEffect(() => {
    // The site key is public runtime config served by the backend.
    api.get("/auth/public-config")
      .then(({ data }) => setSiteKey(data.turnstile_site_key ?? null))
      .catch(() => setSiteKey(null));
  }, []);

  useEffect(() => {
    if (!siteKey || !widgetRef.current) return;
    const render = () => {
      if (window.turnstile && widgetRef.current) {
        window.turnstile.render(widgetRef.current, {
          sitekey: siteKey,
          callback: (token) => { tokenRef.current = token; },
        });
      }
    };
    if (window.turnstile) {
      render();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = render;
    document.head.appendChild(script);
  }, [siteKey]);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  async function lookupAres() {
    const ico = form.company_id.trim();
    if (ico.length < 6) {
      setAresStatus("error");
      setAresMsg(t("suppliers.icoMinLength"));
      return;
    }
    setAresStatus("loading");
    setAresMsg(t("suppliers.aresSearching"));
    try {
      const { data } = await api.get<AresResult>(`/ares/lookup?ico=${encodeURIComponent(ico)}`);
      setForm((prev) => ({
        ...prev,
        company_name: data.obchodni_jmeno || prev.company_name,
        vat_id: data.dic || prev.vat_id,
        billing_address: data.address_street || prev.billing_address,
        billing_city: data.city || prev.billing_city,
        billing_postal_code: data.postal_code || prev.billing_postal_code,
        billing_country: data.country || prev.billing_country,
        company_id: data.ico,
      }));
      setAresStatus("ok");
      setAresMsg(t("suppliers.aresFound", { name: data.obchodni_jmeno }));
    } catch (e: any) {
      const s = e?.response?.status;
      setAresStatus(s === 404 ? "notfound" : "error");
      setAresMsg(s === 404 ? t("suppliers.aresNotFound") : t("suppliers.aresError"));
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setOk(null);
    if (form.password.length < 8) { setErr(t("auth.passwordTooShort")); return; }
    if (form.password !== form.password2) { setErr(t("auth.passwordsMismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/register", { ...form, seed_demo: seedDemo, turnstile_token: tokenRef.current });
      setOk(t("auth.registerOk"));
      setTimeout(() => nav("/login?registered=1"), 3000);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t("auth.registerError"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", background: "var(--bg)", position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "fixed", top: 16, right: 16, zIndex: 10 }}>
        <LanguageFlags />
      </div>
      <div style={{
        position: "absolute", top: "-20%", left: "-10%",
        width: 600, height: 600, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(255,183,0,0.08) 0%, transparent 70%)",
        filter: "blur(60px)",
      }} />

      <div style={{ position: "relative", width: "100%", maxWidth: 520, padding: "40px 24px" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Logo size={40} />
            <span style={{
              fontSize: 24, fontWeight: 800, letterSpacing: -0.5,
              background: "var(--gradient-sunset)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>LuigiPOS</span>
          </div>
        </div>

        <div className="card card-glow" style={{ padding: 32 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{t("auth.registerTitle")}</h1>
          <p style={{ color: "var(--text-secondary)", fontSize: 14, marginBottom: 24 }}>
            {t("auth.registerSubtitle")}
          </p>

          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label>{t("auth.email")}</label>
              <input placeholder="admin@firma.cz" value={form.email} onChange={set("email")} required />
            </div>
            <div className="grid grid-2">
              <div>
                <label>{t("auth.passwordMin")}</label>
                <input type="password" placeholder="••••••••" value={form.password} onChange={set("password")} required />
              </div>
              <div>
                <label>{t("auth.password2")}</label>
                <input type="password" placeholder="••••••••" value={form.password2} onChange={set("password2")} required />
              </div>
            </div>

            <div>
              <label>{t("auth.fullName")}</label>
              <input placeholder={t("auth.fullNamePh")} value={form.full_name} onChange={set("full_name")} />
            </div>

            <hr />

            <div>
              <label>IČO <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 400 }}>({t("auth.optional")})</span></label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  placeholder="12345678" value={form.company_id}
                  onChange={set("company_id")} onBlur={lookupAres}
                  style={{ flex: 1 }}
                />
                <button type="button" onClick={lookupAres} disabled={aresStatus === "loading"} style={{ flexShrink: 0 }}>
                  {aresStatus === "loading" ? <IconLoader size={18} /> : <><IconSearch size={18} /> ARES</>}
                </button>
              </div>
              <p style={{ color: "var(--muted)", fontSize: 12, margin: "6px 0 0" }}>
                {t("auth.aresHint")}
              </p>
              {aresMsg && (
                <div style={{
                  marginTop: 8, display: "flex", alignItems: "center", gap: 6, fontSize: 13,
                  color: aresStatus === "ok" ? "var(--green)" : aresStatus === "notfound" ? "var(--yellow)" : "var(--red)",
                }}>
                  {aresStatus === "ok" && <IconCheck size={14} />}
                  {aresMsg}
                </div>
              )}
            </div>

            <div>
              <label>{t("auth.companyName")}</label>
              <input placeholder={t("auth.companyNamePh")} value={form.company_name} onChange={set("company_name")} required />
            </div>
            <div>
              <label>DIČ <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 400 }}>({t("auth.optional")})</span></label>
              <input placeholder="CZ12345678" value={form.vat_id} onChange={set("vat_id")} />
            </div>

            <hr />
            <h3 style={{ fontSize: 15, fontWeight: 600, color: "var(--text-secondary)" }}>{t("auth.billingAddress")}</h3>

            <div>
              <label>{t("auth.street")}</label>
              <input placeholder={t("auth.streetPh")} value={form.billing_address} onChange={set("billing_address")} />
            </div>
            <div className="grid grid-2">
              <div>
                <label>{t("suppliers.city")}</label>
                <input placeholder="Praha" value={form.billing_city} onChange={set("billing_city")} />
              </div>
              <div>
                <label>{t("suppliers.postalCode")}</label>
                <input placeholder="110 00" value={form.billing_postal_code} onChange={set("billing_postal_code")} />
              </div>
            </div>

            <label style={{
              display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer",
              padding: "12px 14px", borderRadius: "var(--radius-sm)",
              border: "1px solid var(--border)", background: "var(--bg-elevated)",
            }}>
              <input
                type="checkbox" checked={seedDemo}
                onChange={(e) => setSeedDemo(e.target.checked)}
                style={{ width: "auto", marginTop: 2 }}
              />
              <span style={{ fontSize: 14 }}>
                <strong>{t("auth.seedDemo")}</strong>
                <span style={{ display: "block", color: "var(--muted)", fontSize: 12, marginTop: 2 }}>
                  {t("auth.seedDemoDesc")}
                </span>
              </span>
            </label>

            {err && (
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "10px 14px", borderRadius: "var(--radius-sm)",
                background: "var(--red-dim)", color: "var(--red)", fontSize: 13,
              }}>
                <IconAlert size={16} /> {err}
              </div>
            )}
            {ok && (
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "10px 14px", borderRadius: "var(--radius-sm)",
                background: "var(--green-dim)", color: "var(--green)", fontSize: 13,
              }}>
                <IconCheck size={16} /> {ok}
              </div>
            )}

            {siteKey && <div ref={widgetRef} />}

            <button type="submit" className="primary" disabled={loading} style={{ marginTop: 4 }}>
              {loading ? <IconLoader size={18} /> : t("auth.createAccount")}
            </button>
          </form>

          <p style={{ marginTop: 24, textAlign: "center", color: "var(--text-secondary)", fontSize: 14 }}>
            {t("auth.haveAccount")} <Link to="/login" style={{ color: "var(--accent)", fontWeight: 600 }}>{t("auth.signIn")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
